<template>
  <form class="login-form" @submit.prevent="proceed" v-show="loaded">
    <div class="content-section">
      <img
        src="../assets/images/capitecpay.png"
        alt="Capitec pay"
        class="capitecpay-logo"
      />
      <h3>
        Enter the {{ activeInputTextLowercase }} linked to your Capitec account
      </h3>
      <p>
        You will receive a notification in your Capitec app, or a pop up message
        on your phone to authorise the payment.
      </p>
    </div>
    <div class="inputs-section">
      <!-- User Input -->
      <div class="input-block">
        <pf-input
          id="capitec"
          class="capitec-input"
          :placeholder="`Enter ${activeInputTextLowercase}`"
          v-model="capitecInput"
          :disabled="isCapitecHighRisk"
        >
          {{ getActiveInputText }}
        </pf-input>
      </div>
    </div>

    <div class="inputs-switch" v-if="!isCapitecHighRisk">
      <p>I would like to use my</p>
      <span
        v-for="(option, key) in inputOptions.filter(({ active }) => !active)"
        :key="key"
      >
        <span @click="inputSwitch(option.identifierKey)" class="underlined">{{
          option.label
        }}</span>
        <!-- Minus 2 from count to take into account that 1 option will be active -->
        <span v-if="key !== inputOptions.length - 2"> | </span>
      </span>
    </div>

    <div class="pay-button">
      <proceed-button
        :is-proceed-enable="canProceed()"
        :is-loading="this.isLoading"
      />
    </div>
    <div class="notification">
      <p class="note">
        <span><img src="../assets/images/info.svg" alt="info" /></span> Please
        note
      </p>
      <p class="disclaimer">None of your bank credentials will be stored.</p>
    </div>
  </form>
</template>

<script>
import { formMixin } from '@/mixins/formMixin.js'
import { mapActions, mapGetters, mapState } from 'vuex'
import pfInput from '../components/CustomInput.vue'
import Constants from '@/constants'
import axios from '@/wrapper'

export default {
  name: 'CapitecBankForm',
  mixins: [formMixin],
  components: {
    pfInput,
  },
  data() {
    return {
      loaded: false,
      countdown: 120,
      inputOptions: Constants.capitecPayInputOptions,
      capitecInput: '',
      maskedInput: '',
    }
  },
  computed: {
    ...mapState('transaction', ['transaction', 'capitecPayInputType']),
    ...mapGetters('merchantSettings', ['isCapitecHighRiskTransaction']),
    amount() {
      return this.transaction && this.transaction.amount
    },
    isProceedEnable() {
      return this.canProceed()
    },
    currency() {
      const currency = this.transaction && this.transaction.currency
      if (currency && currency.toLowerCase() === 'zar') {
        return 'R'
      }
      return currency
    },
    getActiveInputText() {
      return this.getActiveOption().label
    },
    activeInputTextLowercase() {
      if (this.getActiveOption().label === 'ID Number') {
        return 'ID number'
      }

      return this.getActiveOption().label.toLowerCase()
    },
    isCapitecHighRisk() {
      return this.isCapitecHighRiskTransaction
    },
  },
  methods: {
    ...mapActions('transaction', ['setCapitecPayInputType']),
    getActiveOption() {
      return this.inputOptions.find((option) => option.active)
    },
    setCapitecOptions() {
      let activeInputType = this.capitecPayInputType

      if (this.isCapitecHighRiskTransaction) {
        this.capitecInput = this.maskInput(this.transaction.idNumber)
        this.setCapitecPayInputType(Constants.capitecPayInputConfig.id.key)
        activeInputType = Constants.capitecPayInputConfig.id.key
      }

      this.setActiveOption(activeInputType)
    },
    inputSwitch(key) {
      this.setActiveOption(key)
      this.setCapitecPayInputType(key)
    },
    setActiveOption(key) {
      this.inputOptions = this.inputOptions.map((option) => ({
        ...option,
        active: option.identifierKey === key,
      }))
    },
    getSelectedInputOption() {
      return Constants.capitecPayInputOptions.find(
        ({ identifierKey }) => identifierKey === this.capitecPayInputType
      )
    },
    validateRegex(regex) {
      const input = this.isCapitecHighRiskTransaction
        ? this.transaction.idNumber
        : this.capitecInput
      return regex.test(input)
    },
    canProceed() {
      const selectedOption = this.getSelectedInputOption()
      return this.validateRegex(selectedOption.regex)
    },
    maskInput(val) {
      let maskedInput = ''
      for (let i = 0; i < val.length; i++) {
        maskedInput += i > 1 && i < 9 ? '*' : val.split('')[i]
      }

      return maskedInput
    },
    async proceed() {
      if (!this.canProceed()) {
        return false
      }
      this.$emit('closeErrorMessage')
      let errorMessage = ''
      this.isLoading = true
      this.isShowCapitecError = false
      const identifierValue = this.isCapitecHighRiskTransaction
        ? this.transaction.idNumber
        : this.capitecInput

      const bankApiDetails = {
        identifierValue,
        identifierKey: this.capitecPayInputType,
        amount: parseInt(this.transaction.amount * 100),
      }
      await axios
        .post(
          `v30/BankApi/InitiateBank/${this.transaction.id}`,
          {
            acquirerCode: this.transaction.acquirerCode,
            requestDetails: JSON.stringify(bankApiDetails),
          },
          {}
        )
        .then(function (response) {
          if (response.data.HasErrors)
            errorMessage =
              response.data.ErrorMessages[0].ErrorMessage ??
              'Login failed. Please try again.'
        })
        .catch(function () {
          errorMessage = 'Login failed. Please try again.'
        })

      if (errorMessage !== '') {
        this.$emit('setError', errorMessage)
        this.isLoading = false
      }
      return true
    },
  },

  async mounted() {
    this.loaded = true
    this.setCapitecOptions()
  },
}
</script>

<style lang="scss" scoped>
$forestGreen300: #439292;
$grey: #545a5a;

#login-form {
  margin-top: 24px;
}

.content-section {
  margin-top: 28px;

  h3 {
    font-weight: bold;
    font-size: 1.2em;
    text-align: center;
    margin-top: 24px;
  }

  p {
    font-weight: 400;
    font-size: 1em;
    line-height: 1.2;
    text-align: center;
  }
}

.inputs-section {
  margin-top: 24px;
}

.capitecpay-logo {
  display: block;
  margin: 0 auto;
}

.inputs-switch {
  font-weight: 400;
  font-size: 1em;
  text-align: center;
  margin-top: 24px;

  .underlined {
    text-decoration: underline;
    cursor: pointer;
    color: #0069b1;
    font-weight: bold;
    font-size: 1em;
  }
}

.pay-button {
  display: flex;
  justify-content: center;
}

.countdown {
  font-weight: bold;
  font-size: 18px;
  color: $grey;
  margin-top: 36px;
}

.notification {
  margin: 0 auto;
  text-align: center;
  border-radius: 8px;
  background: #d2cdcd1a;
  padding: 1px 20px;
  font-size: 0.9em;
  margin-top: 24px;

  .note {
    font-weight: bold;

    span {
      margin-right: 2px;
    }
  }

  .disclaimer {
    font-size: 0.9em;
  }
}

.modal-steps {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__1,
  &__2,
  &__3 {
    display: flex;
    align-items: center;
    padding-top: 16px;

    img {
      margin-right: 8px;
    }

    h3 {
      font-weight: bold;
      font-size: 18px;
    }
  }

  a {
    font-weight: 400;
    font-size: 16px;
    color: $forestGreen300;
    text-decoration: none;
  }

  span {
    font-weight: 600;
    font-size: 16px;
    padding-top: 48px;
    padding-bottom: 12px;
  }
}

.input-block {
  align-items: center;
}
</style>
